import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { User, LoginCredentials, SignupData, UpdateProfilePayload } from '../types';
import { authAPI } from '../services/api';
import { trackEvent } from '../utils/analytics';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (credentials: LoginCredentials) => Promise<User | null>;
  signup: (data: SignupData) => Promise<User | null>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  refreshUser: () => Promise<User | null>;
  updateProfile: (data: UpdateProfilePayload) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const checkAuthStatus = useCallback(async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.data.user);
      return response.data.user as User;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    setLoading(true);
    try {
      await authAPI.login(credentials);
      const u = await checkAuthStatus();
      if (u) {
        trackEvent('login', { method: 'email' });
      }
      return u;
    } catch (error) {
      console.error('AuthContext: Login error:', error);
      trackEvent('login_error', { method: 'email' });
      setLoading(false);
      throw error;
    }
  }, [checkAuthStatus]);

  const signup = useCallback(async (data: SignupData) => {
    setLoading(true);
    try {
      await authAPI.signup(data);
      const u = await checkAuthStatus();
      if (u) {
        trackEvent('sign_up', { method: 'email' });
      }
      return u;
    } catch (error) {
      setLoading(false);
      trackEvent('sign_up_error', { method: 'email' });
      throw error;
    }
  }, [checkAuthStatus]);

  const logout = useCallback(async () => {
    setLoading(true);
    try {
      await authAPI.logout();
      setUser(null);
      trackEvent('logout');
    } catch (error) {
      // Even if logout fails, clear local state
      setUser(null);
      trackEvent('logout_error');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUser = useCallback(async () => {
    const u = await checkAuthStatus();
    return u;
  }, [checkAuthStatus]);

  const updateProfile = useCallback(async (data: UpdateProfilePayload) => {
    try {
      const response = await authAPI.updateProfile(data);
      setUser(response.data.user);
      trackEvent('profile_update');
    } catch (error) {
      trackEvent('profile_update_error');
      throw error;
    }
  }, []);

  const value: AuthContextType = useMemo(() => ({
    user,
    loading,
    login,
    signup,
    logout,
    isAuthenticated: !!user,
    refreshUser,
    updateProfile,
  }), [user, loading, login, signup, logout, refreshUser, updateProfile]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
